import { observer } from 'mobx-react'
import { FC, useContext, useEffect, useMemo, useState } from 'react'
import { Side, Tab } from '@store/CodeView.store'
import { JSONEditor } from '@components/JSONEditor/JSONEditor'
import { getClient } from '@communication/Configurations'
import { StoreContext } from '@components/App'
import { ContentLoader } from '../../TabbedCodeView.styles'
import { checkIfUserCanEditResource, checkIfUserCanDeleteResource } from '@communication/Permissions'
import { LifecycleState } from '@customTypes/General'
import { TabError } from '../TabError/TabError'
import { EntityVersion } from '@/types/Entity'

interface ClientWindowProps {
  side: Side
}

export const ClientWindow: FC<ClientWindowProps> = observer(({ side }) => {
  const store = useContext(StoreContext)
  const { windowsActiveTabs } = store.CodeViewState
  const { entityList } = store.CommunicationState
  const tabData = useMemo(() => windowsActiveTabs[side] as Tab, [windowsActiveTabs, side])

  const [lifecycleState, setLifecycleState] = useState<LifecycleState>('init')
  const [errorMessage, setErrorMessage] = useState<string>('')

  const fetchClient = async () => {
    setLifecycleState('fetching')
    const res = await getClient(tabData?.clientId)

    if (res?.success) {
      store.CodeViewState.setTabConfigurationData({ tabId: tabData?.id, configurationData: res?.data })
      store.CodeViewState.updateTabValue({ tabId: tabData?.id, shouldRefetchData: false })
      setEditorKey((prev) => prev + 1)
      setLifecycleState('fetched')
    } else {
      setErrorMessage(res?.error?.message || 'Failed to fetch client')
      setLifecycleState('error')
    }
  }

  const refreshTab = async () => {
    await fetchClient()
    setEditorKey((prev) => prev + 1)
  }

  const checkEditRights = async () => {
    const canEditRes = await checkIfUserCanEditResource(tabData?.clientId)
    if (canEditRes?.data?.authorized) {
      store.CodeViewState.updateTabValue({ tabId: tabData?.id, hasEditRights: true })
    }
  }

  const checkDeleteRights = async () => {
    const canDelete = await checkIfUserCanDeleteResource(tabData?.clientId)
    if (canDelete?.data?.authorized) {
      store.CodeViewState.updateTabValue({ tabId: tabData?.id, hasDeleteRights: true })
    }
  }

  useEffect(() => {
    if (tabData?.configurationData?.current?.clientId && tabData?.clientId) {
      setEditorKey((prev) => prev + 1)
      setLifecycleState('fetched')
    } else if (tabData?.clientId) {
      fetchClient()
      checkEditRights()
      checkDeleteRights()
    } else {
      setErrorMessage('Client ID not found')
      setLifecycleState('error')
    }
  }, [tabData?.clientId, tabData?.configurationData?.current?.clientId, tabData?.id])

  const handleJSONEditorChange = (e, val) => {
    if (val) {
      store.CodeViewState.setTabJsonData({ tabId: tabData?.id, jsonObject: val })
    }
  }

  const [editorKey, setEditorKey] = useState(0)

  useEffect(() => {
    setEditorKey((prev) => prev + 1)
  }, [tabData?.isEditing])

  useEffect(() => {
    if (tabData?.shouldRefetchData) {
      refreshTab()
    }
  }, [tabData?.shouldRefetchData])

  const JSONPropIconOnclick = (propertyToPreviewPathName: string) => {
    let baseString = 'current/'
    if (propertyToPreviewPathName === 'current') {
      baseString = ''
    }

    const refMap = tabData?.configurationData?.referenceMapping as Array<{
      unresolvedPath: string
      resolvedValue: object
    }>

    const object = refMap.find((item) => item.unresolvedPath === baseString + propertyToPreviewPathName)
      ?.resolvedValue as EntityVersion

    const jsonObject = JSON.stringify(object, null, 4)

    const clientId = object?.clientId

    if (entityList?.filter((item) => item.name === clientId)?.length) {
      store.CodeViewState.addTab({
        tab: {
          title: clientId,
          type: 'entity',
          clientId,
        },
        side,
      })
    } else {
      store.CodeViewState.addTab({
        tab: {
          type: 'json-preview',
          title: propertyToPreviewPathName,
          jsonObject,
        },
        side,
      })
    }
  }

  switch (lifecycleState) {
    case 'init':
      return <></>
    case 'fetching':
      return <ContentLoader type="code" />
    case 'fetched':
      return (
        <JSONEditor
          key={editorKey}
          height="100%"
          width="100%"
          value={tabData?.jsonObject}
          onChange={tabData?.isEditing ? handleJSONEditorChange : undefined}
          widgetOnClick={JSONPropIconOnclick}
          widgetPropNameList={tabData?.configurationData?.referenceMapping?.map((item) => item.unresolvedPath)}
        />
      )
    case 'error':
      return <TabError endpoint={'/configuration'} resourceType={tabData?.clientId} errorMessage={errorMessage} />
  }
})
